import { useState, useEffect } from "react";

import { themeLight, themeDark } from "../constants";

type ResolvedTheme = typeof themeLight | typeof themeDark;

/**
 * Hook that returns the currently resolved theme (light or dark).
 * This reads from the data-theme attribute on the document element
 * and listens for changes via MutationObserver.
 *
 * @returns The resolved theme - always "light" or "dark", never "system"
 */
export const useResolvedTheme = (): ResolvedTheme => {
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    // Initial value from document
    const currentTheme = document.documentElement.dataset.theme;
    return currentTheme === themeDark ? themeDark : themeLight;
  });

  useEffect(() => {
    // Observer to watch for changes to the data-theme attribute
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-theme"
        ) {
          const newTheme = document.documentElement.dataset.theme;
          setResolvedTheme(newTheme === themeDark ? themeDark : themeLight);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return resolvedTheme;
};
